// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useLazyQuery, useMutation } from '@apollo/client';
import { useLocalStorage } from 'common/hooks';
import { addToast } from 'helpers/toasts';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { CopilotMessageFeedbackEnum } from 'schema';
import COPILOT_CONVERSATION from 'queries/CopilotConversation.graphql';
import CLEAR_COPILOT_CONVERSATION from 'queries/ClearCopilotConversation.graphql';
import OPEN_COPILOT_CONVERSATION from 'queries/OpenCopilotConversation.graphql';

export type Answer = {
    role: 'assistant' | 'user';
    id: number;
    content?: string;
    error?: string;
    feedback?: CopilotMessageFeedbackEnum | null;
    chat_id?: string;
    message_id?: number;
    able_to_answer?: boolean;
    streaming?: boolean;
};

type CopilotContextType = {
    isRightSidebarOpen: boolean;
    setIsRightSidebarOpen: (value: boolean) => void;
    loading: boolean;
    answers: Answer[];
    setAnswers: (value: Answer[] | ((prev: Answer[]) => Answer[])) => void;
    clearHistory: () => Promise<void>;
    openFromSearch: (question: string, answer: Answer | undefined) => Promise<void>;
    pendingQuery: string | null;
    setPendingQuery: (value: string | null) => void;
};

export const CopilotContext = createContext<CopilotContextType>({
    isRightSidebarOpen: false,
    setIsRightSidebarOpen: () => { },
    loading: false,
    answers: [],
    setAnswers: () => { },
    clearHistory: () => Promise.resolve(),
    openFromSearch: () => Promise.resolve(),
    pendingQuery: null,
    setPendingQuery: () => { },
});

type CopilotContextProviderProps = {
    children: React.ReactNode;
}

export const CopilotContextProvider = ({ children }: CopilotContextProviderProps) => {
    const [isRightSidebarOpen, setIsRightSidebarOpen] = useLocalStorage('isRightSidebarOpen', false);
    const [answers, setAnswers] = useState<Answer[]>([]);
    const [pendingQuery, setPendingQuery] = useState<string | null>(null);

    const [loadConversation, { loading: loadingConversation, data, error: loadError }] = useLazyQuery(COPILOT_CONVERSATION);
    const [clearConversation, { loading: loadingClearConversation, error: clearError }] = useMutation(CLEAR_COPILOT_CONVERSATION, {
        refetchQueries: [{ query: COPILOT_CONVERSATION }],
    });
    const [openConversation, { loading: loadingFromSearch, error: errorFromSearch }] = useMutation(OPEN_COPILOT_CONVERSATION, {
        refetchQueries: [{ query: COPILOT_CONVERSATION }],
    });
    const loading = useMemo(
        () => loadingConversation || loadingClearConversation || loadingFromSearch,
        [loadingConversation, loadingClearConversation, loadingFromSearch],
    );

    const clearHistory = useCallback(async () => {
        setAnswers([]);
        await clearConversation();
    }, [clearConversation]);

    useEffect(() => {
        if (clearError) {
            addToast({ intent: 'danger', message: String(clearError) });
        }
    }, [clearError]);

    const openFromSearch = useCallback(async (question: string, answer: Answer | undefined) => {
        if (answer && answer.able_to_answer && !answer.error) {
            await openConversation({ variables: { input: { question, answer: answer.content ?? '' } } });
        } else if (!answer) {
            setPendingQuery(question);
        }
        setIsRightSidebarOpen(true);
    }, [openConversation, setIsRightSidebarOpen]);

    useEffect(() => {
        if (errorFromSearch) {
            addToast({ intent: 'danger', message: String(errorFromSearch) });
        }
    }, [errorFromSearch]);

    useEffect(() => {
        if (!data) {
            return;
        }
        setAnswers(
            data.viewer.copilotConversation.messages.map(message => ({
                id: parseInt(message.id),
                role: message.role,
                content: message.content ?? undefined,
                feedback: message.feedback,
                chat_id: data.viewer.copilotConversation.externalId,
                message_id: message.externalId,
            })),
        );
    }, [data]);

    useEffect(() => {
        if (loadError) {
            addToast({ intent: 'danger', message: String(loadError) });
        }
    }, [loadError]);

    useEffect(() => {
        if (isRightSidebarOpen) {
            loadConversation();
        }
    }, [isRightSidebarOpen, loadConversation]);

    const contextValue = useMemo(() => ({
        isRightSidebarOpen,
        setIsRightSidebarOpen,
        loading,
        answers,
        setAnswers,
        clearHistory,
        openFromSearch,
        pendingQuery,
        setPendingQuery,
    }), [isRightSidebarOpen, setIsRightSidebarOpen, loading, answers, setAnswers, clearHistory, openFromSearch, pendingQuery]);

    return (
        <CopilotContext.Provider value={contextValue}>
            {children}
        </CopilotContext.Provider>
    );
};
